import { createConsumer } from '@rails/actioncable';
import * as Sentry from '@sentry/vue';

const PRESENCE_INTERVAL = 20000;
const RECONNECT_INTERVAL = 1000;
const MAX_RECONNECT_INTERVAL = 30000;
const MAX_RECONNECT_ATTEMPTS_BEFORE_REBUILD = 5;

class BaseActionCableConnector {
  static isDisconnected = false;

  constructor(
    app,
    pubsubToken,
    websocketHost = '',
    presenceInterval = PRESENCE_INTERVAL
  ) {
    this.websocketURL = websocketHost ? `${websocketHost}/cable` : undefined;
    this.pubsubToken = pubsubToken;
    this.app = app;
    this.events = {};
    this.reconnectTimer = null;
    this.reconnectAttempts = 0;
    this.presenceTimer = null;
    this.isDisconnecting = false;
    this.isRebuilding = false;
    this.isAValidEvent = () => true;

    this.buildConnection();

    this.triggerPresenceInterval = () => {
      this.presenceTimer = setTimeout(() => {
        this.subscription.updatePresence();
        this.triggerPresenceInterval();
      }, presenceInterval);
    };
    this.triggerPresenceInterval();
  }

  buildConnection() {
    const consumer = createConsumer(this.websocketURL);
    this.consumer = consumer;
    this.subscription = consumer.subscriptions.create(
      {
        channel: 'RoomChannel',
        pubsub_token: this.pubsubToken,
        account_id: this.app.$store.getters.getCurrentAccountId,
        user_id: this.app.$store.getters.getCurrentUserID,
      },
      {
        updatePresence() {
          this.perform('update_presence');
        },
        received: this.onReceived,
        connected: () => {
          if (this.isDisconnecting || consumer !== this.consumer) {
            return;
          }

          this.reconnectAttempts = 0;
          this.clearReconnectTimer();

          if (BaseActionCableConnector.isDisconnected) {
            BaseActionCableConnector.isDisconnected = false;
            this.onReconnect();
          }
        },
        disconnected: () => {
          if (
            this.isDisconnecting ||
            this.isRebuilding ||
            consumer !== this.consumer
          ) {
            return;
          }

          BaseActionCableConnector.isDisconnected = true;
          this.onDisconnected();
          this.initReconnectTimer();
        },
      }
    );
  }

  rebuildConnection() {
    this.clearReconnectTimer();
    this.isRebuilding = true;
    try {
      this.consumer.disconnect();
    } catch {
      // ignore disconnect errors during rebuild
    }
    this.buildConnection();
    this.isRebuilding = false;
    this.initReconnectTimer();
  }

  checkConnection() {
    if (this.isDisconnecting || !BaseActionCableConnector.isDisconnected) {
      this.clearReconnectTimer();
      return;
    }

    this.reconnectAttempts += 1;
    if (this.reconnectAttempts >= MAX_RECONNECT_ATTEMPTS_BEFORE_REBUILD) {
      this.reconnectAttempts = 0;
      this.rebuildConnection();
      return;
    }

    // ActionCable only confirms a working subscription via `connected()`.
    this.consumer.connection.reopen();
    this.initReconnectTimer();
  }

  clearReconnectTimer = () => {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  };

  initReconnectTimer = () => {
    this.clearReconnectTimer();
    const delay = Math.min(
      RECONNECT_INTERVAL * 2 ** this.reconnectAttempts,
      MAX_RECONNECT_INTERVAL
    );
    this.reconnectTimer = setTimeout(() => {
      this.checkConnection();
    }, delay);
  };

  // eslint-disable-next-line class-methods-use-this
  onReconnect = () => {};

  // eslint-disable-next-line class-methods-use-this
  onDisconnected = () => {};

  disconnect() {
    this.isDisconnecting = true;
    BaseActionCableConnector.isDisconnected = false;
    this.reconnectAttempts = 0;
    this.clearReconnectTimer();
    if (this.presenceTimer) {
      clearTimeout(this.presenceTimer);
      this.presenceTimer = null;
    }
    this.consumer.disconnect();
  }

  onReceived = ({ event, data } = {}) => {
    if (this.isAValidEvent(data)) {
      if (this.events[event] && typeof this.events[event] === 'function') {
        try {
          this.events[event](data);
        } catch (error) {
          Sentry.setContext('websocket_event', { event });
          Sentry.captureException(error);
        }
      }
    }
  };
}

export default BaseActionCableConnector;
